/**
 * Common Syntactic Validation Patterns
 * Cross-cutting validation rules used across features
 *
 * These are atomic validations that can be composed into
 * feature-specific validation schemas.
 */

import { z } from 'zod/v4'

// ============================================
// String Validations
// ============================================

/**
 * Email validation - common pattern
 */
export const emailValidation = z
  .string()
  .email('Invalid email format')
  .toLowerCase()
  .trim()
  .min(5, 'Email too short')
  .max(254, 'Email too long') // RFC 5321 limit

/**
 * Password validation - canonical COMPOSITION policy, unified for all environments.
 * Policy: 8-128 chars, at least 1 lowercase letter [a-z], at least 1 digit.
 *
 * ⚠️ NO ES LA POLÍTICA ENTERA. El registro y el cambio de contraseña pasan
 * además por `password-policy.ts` (secuencias obvias y contraseñas comunes).
 * Leer sólo este esquema y llamarlo «la política» es lo que dejó la lista de
 * requisitos del registro enseñando tres reglas de cinco.
 *
 * NIST-aligned: NO character whitelist. Any character is allowed — "ñ", accented
 * letters, spaces, and symbols like #._- must never invalidate a password.
 * (The previous whitelist regex rejected "Contraseña2026!" because of the "ñ",
 * while blaming the lowercase/number rule.)
 *
 * ASCII [a-z] and \d are intentional (NOT Unicode \p{Ll}): the backend
 * password-validator.service.ts and the frontend PasswordRequirements checklist
 * test with the same ASCII classes, so all layers stay exactly aligned.
 * Uppercase/symbols are NOT required (NIST prefers length over composition).
 *
 * Each rule is a separate .regex() so the error message names the exact rule
 * that failed instead of a combined message.
 */
export const passwordValidation = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password too long')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/\d/, 'Password must contain at least one number')

/**
 * Name validation - for first/last names
 */
export const nameValidation = z
  .string()
  .min(1, 'Name is required')
  .max(50, 'Name too long')
  .trim()
  .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Name can only contain letters, spaces, apostrophes, and hyphens')

/**
 * Phone number validation
 */
export const phoneValidation = z
  .string()
  .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format (use E.164 format)')

/**
 * UUID validation
 */
export const uuidValidation = z.string().uuid('Invalid UUID format')

/**
 * URL validation
 */
export const urlValidation = z.string().url('Invalid URL format').max(2048, 'URL too long')

/**
 * Base64 validation
 */
export const base64Validation = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/, 'Invalid base64 format')

/**
 * Generic text field validation
 */
export const textFieldValidation = (minLength = 0, maxLength = 255) =>
  z
    .string()
    .min(minLength, `Must be at least ${minLength} characters`)
    .max(maxLength, `Must be at most ${maxLength} characters`)
    .trim()

/**
 * Optional text field
 */
export const optionalTextFieldValidation = (maxLength = 255) =>
  z.string().max(maxLength, `Must be at most ${maxLength} characters`).trim().optional()

// ============================================
// Localization Validations
// ============================================

/**
 * Timezone validation
 */
export const timezoneValidation = z
  .string()
  .min(1, 'Timezone required')
  .refine((tz) => {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: tz })
      return true
    } catch {
      return false
    }
  }, 'Invalid timezone')

/**
 * Language code validation (ISO 639-1)
 */
export const languageValidation = z
  .string()
  .regex(/^[a-z]{2}$/, 'Invalid language code (use ISO 639-1 format like "en", "es")')

/**
 * Currency code validation (ISO 4217)
 */
export const currencyValidation = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Invalid currency code (use ISO 4217 format like "USD", "EUR")')

/**
 * Country code validation (ISO 3166-1 alpha-2)
 */
export const countryCodeValidation = z
  .string()
  .length(2, 'Country code must be exactly 2 characters')
  .regex(/^[A-Z]{2}$/, 'Country code must be uppercase letters')

// ============================================
// Enum Validations
// ============================================

/**
 * Theme validation
 */
export const themeValidation = z.enum(
  ['light', 'dark', 'system'],
  'Theme must be light, dark, or system',
)

/**
 * Date format validation
 */
export const dateFormatValidation = z.enum(
  ['DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'],
  'Invalid date format',
)

/**
 * Number format validation
 */
export const numberFormatValidation = z.enum(
  ['europeo-puntos', 'europeo-sin-puntos', 'americano-comas', 'americano-sin-comas'],
  'Invalid number format',
)

/**
 * Role validation
 */
export const roleValidation = z.enum(
  ['user', 'admin', 'viewer'],
  'Role must be user, admin, or viewer',
)

/**
 * User status validation
 */
export const userStatusValidation = z.enum(
  ['active', 'inactive', 'suspended', 'pending'],
  'Invalid user status',
)

// ============================================
// Number Validations
// ============================================

/**
 * Boolean with default
 */
export const booleanWithDefault = (defaultValue: boolean) => z.boolean().default(defaultValue)

/**
 * Positive number validation
 */
export const positiveNumberValidation = z.number().positive('Must be a positive number')

/**
 * Non-negative number validation
 */
export const nonNegativeNumberValidation = z.number().min(0, 'Must be zero or positive')

// ============================================
// Validation Constants
// ============================================

/** Email regex pattern */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Password requirements */
export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

/** File upload limits */
export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

/** Transaction validation limits */
export const MAX_DESCRIPTION_LENGTH = 500
